import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { appointmentAPI } from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { GlassCard, StatusBadge } from '../../components/common/GlassCard';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  Calendar, Clock, MapPin, Check, X, AlertTriangle,
  CalendarDays, CheckCheck, FileText, Ban,
} from 'lucide-react';

// Group statuses for the tab filters
const STATUS_GROUPS = {
  all: null, // no filter
  assigned: ['pending', 'assigned', 'escalated'],
  approved: ['approved'],
  viewing_scheduled: ['viewing_scheduled'],
  completed: ['viewing_completed', 'booking_initiated'],
  rejected: ['rejected', 'cancelled', 'loan_rejected'],
};

const TAB_LABELS = {
  all: 'All',
  assigned: 'Awaiting',
  approved: 'Approved',
  viewing_scheduled: 'Viewing',
  completed: 'Completed',
  rejected: 'Closed',
};

const Appointments = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [scheduleTarget, setScheduleTarget] = useState(null);
  const [viewingDate, setViewingDate] = useState('');
  const [viewingTime, setViewingTime] = useState('');
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [busyId, setBusyId] = useState(null);

  // Refresh whenever the route key changes (handles the "create → navigate
  // back to /my-appointments" case where same component instance is reused).
  useEffect(() => { loadAppointments(); }, [location.key]);

  const loadAppointments = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await appointmentAPI.getAppointments();
      // V4.17.4 — Defensive: a 200 with the wrong shape (e.g. `null`,
      // `{appointments: []}`, or a 500 body that's not JSON) previously
      // silently rendered the empty state — perceived as a "blank page"
      // by first-time customers who'd just created an appointment.
      const data = res?.data;
      if (Array.isArray(data)) {
        setAppointments(data);
      } else if (data && Array.isArray(data.appointments)) {
        // Forward-compat in case the backend wraps later.
        setAppointments(data.appointments);
      } else if (data == null) {
        setAppointments([]);
      } else {
        console.error('[Appointments] unexpected response shape', data);
        setAppointments([]);
        setLoadError('Unexpected response from server. Please retry.');
      }
    } catch (e) {
      console.error('[Appointments] load failed', e);
      const msg = e?.response?.data?.detail || e?.message || 'Failed to load appointments';
      setLoadError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const runAction = async (id, fn, successMsg, errMsg) => {
    setBusyId(id);
    try {
      await fn();
      toast.success(successMsg);
      await loadAppointments();
    } catch (e) {
      toast.error(e?.response?.data?.detail || errMsg);
    } finally {
      setBusyId(null);
    }
  };

  // Status transitions
  const handleAccept = (id) =>
    runAction(id, () => appointmentAPI.approve(id), 'Appointment accepted', 'Failed to accept');

  const handleReject = () => {
    const id = rejectTarget;
    if (!id) return;
    runAction(
      id,
      () => appointmentAPI.reject(id, rejectReason || 'Declined by agent'),
      'Appointment rejected',
      'Failed to reject',
    ).then(() => {
      setRejectTarget(null);
      setRejectReason('');
    });
  };

  const handleScheduleViewing = () => {
    const id = scheduleTarget;
    if (!id || !viewingDate || !viewingTime) {
      toast.error('Date and time are required');
      return;
    }
    runAction(
      id,
      () => appointmentAPI.updateStatus(id, {
        status: 'viewing_scheduled',
        viewing_date: viewingDate,
        viewing_time: viewingTime,
      }),
      `Viewing scheduled for ${viewingDate} ${viewingTime}`,
      'Failed to schedule viewing',
    ).then(() => {
      setScheduleTarget(null);
      setViewingDate('');
      setViewingTime('');
    });
  };

  // One-click confirm: reuse the customer's already-entered preferred date/time
  // instead of asking the agent to re-enter (avoids duplicate effort).
  const handleConfirmViewing = (appt) =>
    runAction(
      appt.id,
      () => appointmentAPI.updateStatus(appt.id, {
        status: 'viewing_scheduled',
        viewing_date: appt.preferred_date,
        viewing_time: appt.preferred_time,
      }),
      `Viewing confirmed for ${appt.preferred_date} ${appt.preferred_time}`,
      'Failed to confirm viewing',
    );

  // Opens the dialog with the customer's preferred date/time pre-filled,
  // allowing the agent to adjust if needed.
  const handleAdjustViewing = (appt) => {
    setViewingDate(appt.preferred_date || '');
    setViewingTime(appt.preferred_time || '');
    setScheduleTarget(appt.id);
  };

  const handleMarkViewingDone = (id) =>
    runAction(
      id,
      () => appointmentAPI.markViewingCompleted(id, 'Viewing completed by agent'),
      'Viewing marked completed',
      'Failed to mark viewing completed',
    );

  const handleInitiateBooking = async (appt) => {
    // V4.19 fix: do NOT change the appointment status until the booking has
    // been successfully submitted. Previously we called
    // `appointmentAPI.initiateBooking(appt.id)` here which prematurely moved
    // the status to `booking_initiated` and hid the "Proceed to Booking"
    // button even when the agent closed the booking form without submitting.
    // The status transition now happens on the backend inside
    // `POST /bookings/with-form` after the booking row is persisted.
    setBusyId(appt.id);
    try {
      const qp = new URLSearchParams();
      qp.set('appointment_id', appt.id);
      if (appt.property_id) qp.set('property_id', appt.property_id);
      if (appt.unit_id) qp.set('unit_id', appt.unit_id);
      navigate(`/bookings/create?${qp.toString()}`);
    } finally {
      setBusyId(null);
    }
  };

  // Filter by active tab
  const filteredAppointments = appointments.filter((appt) => {
    if (activeTab === 'all') return true;
    const group = STATUS_GROUPS[activeTab];
    return group ? group.includes(appt.status) : false;
  });

  const tabCount = (tab) => {
    if (tab === 'all') return appointments.length;
    const group = STATUS_GROUPS[tab];
    return group ? appointments.filter(a => group.includes(a.status)).length : 0;
  };

  const isAgent = user?.role === 'agent';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#4E50FF]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="appointments-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{isAgent ? 'Manage Appointments' : 'My Appointments'}</h1>
        <p className="text-gray-500 mt-1">{isAgent ? 'View and manage appointment requests' : 'Track your viewing appointments'}</p>
      </div>

      {/* V4.17.4 — explicit error state (instead of silently showing the
          empty list when the API call fails — that's what was being
          perceived as a "blank page" by first-time customers). */}
      {loadError && (
        <div
          className="flex items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50/70 px-4 py-3"
          data-testid="appointments-load-error"
        >
          <div className="text-sm text-red-700">
            <strong>Couldn&apos;t load your appointments.</strong> {loadError}
          </div>
          <Button size="sm" variant="outline" onClick={loadAppointments} data-testid="appointments-retry-btn">
            Retry
          </Button>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-100/80">
          {Object.keys(STATUS_GROUPS).map((tab) => (
            <TabsTrigger key={tab} value={tab} className="capitalize" data-testid={`tab-${tab}`}>
              {TAB_LABELS[tab]}
              <Badge variant="secondary" className="ml-1.5 text-[10px] px-1.5 py-0">
                {tabCount(tab)}
              </Badge>
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          {filteredAppointments.length > 0 ? (
            <GlassCard className="overflow-hidden p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-100">
                    <TableHead className="text-gray-500 font-semibold">Property</TableHead>
                    <TableHead className="text-gray-500 font-semibold">{isAgent ? 'Customer' : 'Assigned Agent'}</TableHead>
                    <TableHead className="text-gray-500 font-semibold">Date & Time</TableHead>
                    <TableHead className="text-gray-500 font-semibold">Status</TableHead>
                    {isAgent && <TableHead className="text-gray-500 font-semibold">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAppointments.map((appt) => {
                    const busy = busyId === appt.id;
                    return (
                      <TableRow key={appt.id} className="border-gray-50" data-testid={`appointment-${appt.id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium text-gray-900">{appt.property?.name}</p>
                            <p className="text-sm text-gray-500 flex items-center gap-1">
                              <MapPin className="w-3 h-3" />{appt.property?.location}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            {isAgent ? (
                              <>
                                <p className="font-medium text-gray-900">{appt.customer?.full_name}</p>
                                <p className="text-sm text-gray-500">{appt.customer?.phone}</p>
                              </>
                            ) : appt.handler ? (
                              <>
                                <p className="font-medium text-gray-900">{appt.handler?.full_name}</p>
                                <p className="text-sm text-gray-500">{appt.handler?.agent_code}</p>
                              </>
                            ) : (
                              <>
                                <p className="font-medium text-amber-600">Awaiting Agent</p>
                                <p className="text-sm text-gray-400">Open Lead</p>
                              </>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-gray-700">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            {formatDate(appt.preferred_date)}
                            <Clock className="w-4 h-4 text-gray-400 ml-2" />
                            {appt.preferred_time}
                          </div>
                          {appt.viewing_date && (
                            <div className="text-xs text-blue-600 mt-1">
                              Viewing: {formatDate(appt.viewing_date)} {appt.viewing_time}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={appt.status} />
                          {appt.lead_status === 'open' && (
                            <div className="flex items-center gap-1 text-amber-500 text-xs mt-1">
                              <AlertTriangle className="w-3 h-3" />Open Lead
                            </div>
                          )}
                          {appt.status === 'escalated' && (
                            <div className="flex items-center gap-1 text-orange-500 text-xs mt-1">
                              <AlertTriangle className="w-3 h-3" />Escalated
                            </div>
                          )}
                        </TableCell>
                        {isAgent && (
                          <TableCell>
                            <div className="flex items-center gap-2 flex-wrap">
                              {['pending', 'assigned', 'escalated'].includes(appt.status) && (
                                <>
                                  <Button
                                    size="sm"
                                    disabled={busy}
                                    onClick={() => handleAccept(appt.id)}
                                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                    data-testid={`accept-${appt.id}`}
                                  >
                                    <Check className="w-4 h-4 mr-1" />Accept
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={busy}
                                    onClick={() => setRejectTarget(appt.id)}
                                    className="border-red-200 text-red-600 hover:bg-red-50"
                                    data-testid={`reject-${appt.id}`}
                                  >
                                    <X className="w-4 h-4 mr-1" />Reject
                                  </Button>
                                </>
                              )}
                              {appt.status === 'approved' && (
                                appt.preferred_date && appt.preferred_time ? (
                                  <div className="flex items-center gap-1 flex-wrap">
                                    <Button
                                      size="sm"
                                      disabled={busy}
                                      onClick={() => handleConfirmViewing(appt)}
                                      className="bg-[#4E50FF] hover:bg-[#3436CC] text-white"
                                      data-testid={`confirm-viewing-${appt.id}`}
                                      title={`Confirm viewing for ${appt.preferred_date} ${appt.preferred_time}`}
                                    >
                                      <CalendarDays className="w-4 h-4 mr-1" />Confirm Viewing
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      disabled={busy}
                                      onClick={() => handleAdjustViewing(appt)}
                                      className="text-xs text-gray-600 hover:text-gray-900 px-2"
                                      data-testid={`adjust-viewing-${appt.id}`}
                                    >
                                      Adjust
                                    </Button>
                                  </div>
                                ) : (
                                  <Button
                                    size="sm"
                                    disabled={busy}
                                    onClick={() => setScheduleTarget(appt.id)}
                                    className="bg-[#4E50FF] hover:bg-[#3436CC] text-white"
                                    data-testid={`schedule-${appt.id}`}
                                  >
                                    <CalendarDays className="w-4 h-4 mr-1" />Schedule Viewing
                                  </Button>
                                )
                              )}
                              {appt.status === 'viewing_scheduled' && (
                                <Button
                                  size="sm"
                                  disabled={busy}
                                  onClick={() => handleMarkViewingDone(appt.id)}
                                  className="bg-[#4E50FF] hover:bg-[#3436CC] text-white"
                                  data-testid={`complete-viewing-${appt.id}`}
                                >
                                  <CheckCheck className="w-4 h-4 mr-1" />Mark Viewing Done
                                </Button>
                              )}
                              {appt.status === 'viewing_completed' && (
                                <Button
                                  size="sm"
                                  disabled={busy}
                                  onClick={() => handleInitiateBooking(appt)}
                                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                  data-testid={`initiate-booking-${appt.id}`}
                                >
                                  <FileText className="w-4 h-4 mr-1" />Proceed to Booking
                                </Button>
                              )}
                              {appt.status === 'booking_initiated' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => navigate(`/bookings?appointment=${appt.id}`)}
                                  data-testid={`view-booking-${appt.id}`}
                                >
                                  <FileText className="w-4 h-4 mr-1" />View Booking
                                </Button>
                              )}
                              {['rejected', 'cancelled', 'loan_rejected'].includes(appt.status) && (
                                <span className="text-xs text-gray-400 flex items-center gap-1">
                                  <Ban className="w-3 h-3" />Closed
                                </span>
                              )}
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </GlassCard>
          ) : (
            <GlassCard className="text-center py-12">
              <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Appointments</h3>
              <p className="text-gray-400">
                {activeTab === 'all' ? 'You have no appointments yet' : `No ${TAB_LABELS[activeTab].toLowerCase()} appointments`}
              </p>
            </GlassCard>
          )}
        </TabsContent>
      </Tabs>

      {/* Schedule Viewing Dialog */}
      <Dialog open={!!scheduleTarget} onOpenChange={(open) => !open && setScheduleTarget(null)}>
        <DialogContent data-testid="schedule-viewing-dialog">
          <DialogHeader>
            <DialogTitle>Schedule Viewing</DialogTitle>
            <DialogDescription>
              Set the confirmed date and time for the property viewing. The customer will be notified.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="viewing_date">Viewing Date</Label>
              <Input
                id="viewing_date"
                type="date"
                value={viewingDate}
                onChange={(e) => setViewingDate(e.target.value)}
                data-testid="viewing-date-input"
              />
            </div>
            <div>
              <Label htmlFor="viewing_time">Viewing Time</Label>
              <Input
                id="viewing_time"
                type="time"
                value={viewingTime}
                onChange={(e) => setViewingTime(e.target.value)}
                data-testid="viewing-time-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleTarget(null)}>Cancel</Button>
            <Button
              onClick={handleScheduleViewing}
              className="bg-[#4E50FF] hover:bg-[#3436CC] text-white"
              data-testid="confirm-schedule-btn"
            >
              Confirm Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={!!rejectTarget} onOpenChange={(open) => !open && setRejectTarget(null)}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject Appointment</DialogTitle>
            <DialogDescription>
              Optionally provide a reason. The customer will be notified.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label htmlFor="reject_reason">Reason (optional)</Label>
            <Input
              id="reject_reason"
              placeholder="e.g. Not available at requested time"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              data-testid="reject-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              data-testid="confirm-reject-btn"
            >
              Reject Appointment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Appointments;
